import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { FC } from 'react'

import { IndexedCalendar, isDateSelectable } from './dateRangeSelection'
import { DayCell } from './DayCell'
import * as S from './MonthGrid.styles'
import { CalendarDay, HighlightedRange } from './types'

const ISO_DATE = 'yyyy-MM-dd'
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

interface DayStateLabelParams {
  day?: CalendarDay
  isToday: boolean
  highlightLabel?: string
}

// Explains why a day behaves the way it does, for the cell tooltip. Vacant, fully-available days
// need no note; the blocked and turnover cases are the ones worth surfacing.
const getDayStateLabel = ({
  day,
  isToday,
  highlightLabel,
}: DayStateLabelParams): string | undefined => {
  const labels: string[] = []
  if (isToday) labels.push('Today')
  if (highlightLabel) labels.push(highlightLabel)
  if (day && !day.midStayAllowed) {
    labels.push(day.checkOutAllowed ? 'Check-out only' : 'No occupancy')
  }
  return labels.length > 0 ? labels.join(' · ') : undefined
}

interface WithinRangeParams {
  date: string
  range?: HighlightedRange
}

const isWithinRange = ({ date, range }: WithinRangeParams) =>
  !!range && date >= range.startDate && date <= range.endDate

export interface MonthGridProps {
  monthDate: Date
  calendar: IndexedCalendar
  checkInDate?: string
  checkOutDate?: string
  hoveredDate?: string
  highlightedRange?: HighlightedRange
  onSelectDate: (date: string) => void
  onHoverDate?: (date: string) => void
}

export const MonthGrid: FC<MonthGridProps> = ({
  monthDate,
  calendar,
  checkInDate,
  checkOutDate,
  hoveredDate,
  highlightedRange,
  onSelectDate,
  onHoverDate,
}) => {
  const gridDays = eachDayOfInterval({
    start: startOfWeek(startOfMonth(monthDate)),
    end: endOfWeek(endOfMonth(monthDate)),
  })
  const today = new Date()

  // Mid-selection (check-in chosen, check-out pending) constrains cells to valid check-outs and
  // previews the range up to the hovered day while it stays a valid end.
  const selectionAnchor = checkInDate && !checkOutDate ? checkInDate : undefined
  const previewEnd =
    selectionAnchor && hoveredDate && isDateSelectable(calendar, hoveredDate, selectionAnchor)
      ? hoveredDate
      : undefined
  const rangeEnd = checkOutDate ?? previewEnd

  return (
    <S.Month>
      <S.Weekdays>
        {WEEKDAY_LABELS.map(weekday => (
          <S.Weekday key={weekday}>{weekday}</S.Weekday>
        ))}
      </S.Weekdays>
      <S.Days>
        {gridDays.map(gridDay => {
          const date = format(gridDay, ISO_DATE)
          const isToday = isSameDay(gridDay, today)
          const isHighlighted = isWithinRange({ date, range: highlightedRange })
          return (
            <DayCell
              key={date}
              date={date}
              label={gridDay.getDate()}
              isSelectable={isDateSelectable(calendar, date, selectionAnchor)}
              isOutsideMonth={!isSameMonth(gridDay, monthDate)}
              isToday={isToday}
              isHighlighted={isHighlighted}
              tooltip={getDayStateLabel({
                day: calendar.byDate.get(date),
                isToday,
                highlightLabel: isHighlighted ? highlightedRange?.label : undefined,
              })}
              isRangeStart={date === checkInDate}
              isRangeEnd={date === rangeEnd}
              isInRange={!!checkInDate && !!rangeEnd && date > checkInDate && date < rangeEnd}
              onSelect={onSelectDate}
              onHover={onHoverDate}
            />
          )
        })}
      </S.Days>
    </S.Month>
  )
}
